/**
 * /playlists/:playlistId — one playlist's contents.
 *
 * The third of the library's screens and the only one that shows songs in an order the
 * user chose rather than one the app chose. Everything on it acts on the playlist, not
 * on the library: Play starts the queue here, Remove takes a track out of this list, and
 * the songs themselves are untouched by both — which is why the row's control says
 * "Remove from X" and the header's menu says "Delete playlist".
 *
 * Play from a row starts the playlist AT that row (usePlaylists.playPlaylist takes a
 * start index), so pressing the fourth track plays the fourth, fifth, sixth — not the
 * fourth alone. That is the difference between a playlist and a list of songs, and it
 * is the reason playlist playback goes through playerStore's queue rather than through
 * `play`.
 *
 * A TRACK'S TITLE NO LONGER OPENS THE STUDIO. It was a link to /library/:songId, so the
 * most natural press on a playlist row — the name of the song — abandoned the playlist for
 * an editing workspace. It raises the lyrics preview now, matching the Library, and the
 * Studio is an explicit item in the row's More menu.
 *
 * A playlist that no longer exists returns to the list, the same guard the Studio has
 * for a deleted song: a stale link should land somewhere real, not on an empty frame.
 *
 * NOTHING IS PERSISTED YET — see src/api/playlistsApi.ts. The ordering, the contents and
 * the playback are all real; only the reload is not.
 */
import { useEffect } from 'react';
import { ListMusic, Pause, Pencil, Play, Trash2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { ActionMenu, BackLink, Btn, LoadError, LoadSkeleton, SectionHeading } from '../components/ui';
import { PlaylistArtwork, PlaylistTrackRow } from '../components/playlists';
import { PlayingIndicator, QueuePanel } from '../components/player';
import { UNKNOWN_API_ERROR_MESSAGE } from '../api/errors';
import { usePlayer, usePlaylists, useSongs } from '../hooks';
import { queueMatches } from '../lib/playback';
import { isFirstLoad } from '../stores/loadState';
import { useSongsStore } from '../stores/songsStore';
import { useUiStore } from '../stores/uiStore';
import { c } from '../styles/theme';
import { paths } from './paths';

export default function Playlist() {
  const { playlistId } = useParams<{ playlistId: string }>();
  const navigate = useNavigate();

  const { getPlaylist, songsIn, summaryFor, playPlaylist, removeSong, moveSong } = usePlaylists();
  const { songs } = useSongs();
  const player = usePlayer();
  const setRenamePlaylistTarget = useUiStore((s) => s.setRenamePlaylistTarget);
  const setDeletePlaylistTarget = useUiStore((s) => s.setDeletePlaylistTarget);
  const setAddToPlaylistTarget = useUiStore((s) => s.setAddToPlaylistTarget);
  // What a track's title raises now that it is not a link into the Studio — the same
  // uiStore target and the same AppModals dialog the Library and the Home page use.
  const setLyricsTarget = useUiStore((s) => s.setLyricsTarget);
  /*
    THE SONGS' READ STATE, NOT THE PLAYLISTS'. This screen already has its playlist — the
    guard below sends you away if it does not — and what it renders is `songsIn(playlist)`,
    which resolves the playlist's ids against songsStore. So the read that can leave this
    page looking empty is the LIBRARY's: `playlistSongs` drops every id it cannot resolve
    (deliberately, see its comment), and before GET /songs answers it can resolve none of
    them. A playlist with four tracks would render "No tracks in this playlist".
  */
  const songsLoad = useSongsStore((s) => s.load);
  const loadSongs = useSongsStore((s) => s.loadSongs);

  const playlist = getPlaylist(playlistId);

  // A deleted playlist, or a stale link into one. Replace rather than push, so Back
  // does not bounce straight back into the missing playlist.
  useEffect(() => {
    if (playlistId && !playlist) navigate(paths.playlists, { replace: true });
  }, [playlistId, playlist, navigate]);

  if (!playlist) return null;

  const tracks = songsIn(playlist);
  const empty = tracks.length === 0;
  /*
    THE DISTINCTION THIS SCREEN WAS MISSING. `empty` above is "nothing to render", which was
    treated as "the user has added nothing" — but a playlist that HOLDS ids and resolves none
    of them is a different fact, and the only reason for it is that the library has not
    arrived (or failed to). Gated on `songIds.length` so a genuinely empty playlist keeps its
    designed empty state, which the brief requires be left in place.
  */
  const unresolved = empty && playlist.songIds.length > 0;

  /*
    Whether the player is on THIS playlist's queue.

    Compared as the whole order rather than "the loaded song is one of these tracks", for the
    reasons lib/playback.queueMatches sets out. The comparison lives in that module rather
    than here now that the playlists LIST asks the same question of the same store — see
    routes/Playlists.tsx. Two surfaces deciding "is this playlist playing" two ways is how
    they end up disagreeing.
  */
  const playingThis = player.queueIndex >= 0 && queueMatches(player.queue, tracks.map((t) => t.id));

  return (
    <div>
      <BackLink fallbackTo={paths.playlists} fallbackLabel="Playlists" mode="parent" />

      {/*
        The playlist's own header, not the library's: this screen is one playlist, so it
        names it. The artwork sits in the heading rather than above it, so the title and
        the cover read as one thing at every width — see components/playlists/
        PlaylistArtwork for why the cover is the first track's.
      */}
      <div className="lyx-playlist-head">
        <PlaylistArtwork songs={tracks} size={64} radius={12} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <SectionHeading
            as="h1"
            title={playlist.name}
            subtitle={summaryFor(playlist)}
            marginBottom={0}
          />
        </div>

        <div className="lyx-playlist-head-actions">
          {/*
            The header's Play, which pauses and resumes once this playlist is the one
            playing rather than restarting it from track 1.

            Restarting was the old behaviour and it is the same defect the row's control
            had: pressing the most obvious button on the screen while a playlist was
            halfway through track 4 sent it back to the top. Now it only starts a queue
            when there is not already one for this playlist — see `playingThis`.
          */}
          <Btn
            onClick={() =>
              playingThis && player.songId ? player.toggle(player.songId) : playPlaylist(playlist)
            }
            disabled={empty}
          >
            {/* A normal play/pause button — glyph and word both (Batch 6 §13). The waveform
                used to replace the glyph here, which left the screen's primary control
                iconless on exactly the playlist you were listening to. It is beside the
                button now. */}
            {playingThis && !player.paused ? <Pause size={13} /> : <Play size={13} />}
            {playingThis && !player.paused ? 'Pause' : 'Play'}
          </Btn>
          {/* The app's one playing visual — see components/player/PlayingIndicator. Seeded on
              the playlist, so the shape belongs to what the button acts on, and centred in its
              own box because this actions row wraps on a narrow screen. */}
          {playingThis && !player.paused && (
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              <PlayingIndicator seed={playlist.id} playing />
            </span>
          )}
          <ActionMenu
            label={`More actions for ${playlist.name}`}
            items={[
              {
                label: 'Rename playlist',
                ariaLabel: `Rename ${playlist.name}`,
                icon: <Pencil size={13} color={c.text2} />,
                onSelect: () => setRenamePlaylistTarget(playlist),
              },
              {
                label: 'Delete playlist',
                ariaLabel: `Delete ${playlist.name}`,
                icon: <Trash2 size={13} color={c.danger} />,
                onSelect: () => setDeletePlaylistTarget(playlist),
                danger: true,
              },
            ]}
          />
        </div>
      </div>

      {unresolved && isFirstLoad(songsLoad, false) ? (
        // The tracks are known but not yet resolvable. Rows, because that is what is coming.
        <LoadSkeleton rows={Math.min(playlist.songIds.length, 4)} label="Loading these tracks…" />
      ) : unresolved && songsLoad.status === 'error' ? (
        /*
          The library read failed, so this playlist's tracks cannot be shown — and saying "no
          tracks" here would be a lie about the user's own playlist. `children` carries the one
          thing worth adding: the playlist itself is intact, so the failure is the fetch and
          not their data.
        */
        <LoadError
          message={songsLoad.error?.message ?? UNKNOWN_API_ERROR_MESSAGE}
          onRetry={loadSongs}
        >
          <p style={{ fontSize: 12, color: c.text2, margin: '8px 0 0' }}>
            This playlist still has {playlist.songIds.length}{' '}
            {playlist.songIds.length === 1 ? 'track' : 'tracks'}. Nothing has been removed.
          </p>
        </LoadError>
      ) : empty ? (
        /*
          The empty state offers the fastest route back in rather than only explaining
          the situation. "Add tracks" opens the same "Add to playlist" sheet the library
          row's menu opens — against the newest song, because that is the one a user
          who has just made a playlist is most likely reaching for — and the second
          button goes to the library for anything else.

          On a library with no songs at all there is nothing to add, so only the link to
          the library shows: a button whose dialog would open on nothing is worse than
          no button.
        */
        <div style={{ padding: '28px 0 20px', textAlign: 'center' }}>
          <div
            style={{
              width: 40,
              height: 40,
              margin: '0 auto 14px',
              borderRadius: 10,
              background: c.surface2,
              border: `1px solid ${c.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ListMusic size={17} color={c.text3} />
          </div>
          <p
            style={{
              fontFamily: 'Space Grotesk',
              fontWeight: 600,
              fontSize: 14,
              margin: '0 0 4px',
            }}
          >
            No tracks in this playlist
          </p>
          <p style={{ fontSize: 12, color: c.text2, margin: '0 0 18px' }}>
            Add tracks from your library using a song&rsquo;s More menu.
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            {songs.length > 0 && (
              <Btn onClick={() => setAddToPlaylistTarget(songs[0])}>Add a track</Btn>
            )}
            <Btn variant="secondary" onClick={() => navigate(paths.library)}>
              Go to library
            </Btn>
          </div>
        </div>
      ) : (
        <>
          {/*
            The queue, openable, once this playlist is the thing playing.

            Present only while the player is actually on a queue — `queueSongIndex >= 0`
            — because there is nothing to expand otherwise, and a panel that said "1 of
            1" for a single track played from the library would be a second player bar
            for no reason. That is also why it is here rather than in the app shell: this
            is the screen where a queue exists.

            Every handler goes straight to the store the transport and the full-screen
            view use. `onSelect` re-queues the SAME resolved order at a new index rather
            than calling playPlaylist, which would rebuild the queue from the playlist's
            current contents — those two can differ mid-playback, and the whole point of
            showing the queue is that it is the order you are actually hearing.
          */}
          {player.queueSongIndex >= 0 && (
            <QueuePanel
              songs={player.queueSongs}
              currentIndex={player.queueSongIndex}
              playing={!player.paused}
              elapsed={player.elapsed}
              totalLabel={player.durationLabel}
              speed={player.speed}
              onTogglePlay={() => player.songId && player.toggle(player.songId)}
              onSeek={(seconds) => player.songId && player.seek(player.songId, seconds)}
              onPrev={player.hasPrevious ? player.playPrevious : undefined}
              onNext={player.hasNext ? player.playNext : undefined}
              onSelect={(i) =>
                player.playQueue(
                  player.queueSongs.map((s) => s.id),
                  i
                )
              }
            />
          )}

          <div className="lyx-song-grid">
            {tracks.map((song, i) => (
              <PlaylistTrackRow
                key={song.id}
                song={song}
                position={i + 1}
                playing={player.isPlaying(song.id)}
                // Active, not playing, for the highlight: pausing track 3 of 8 leaves you
                // on track 3. See PlaylistTrackRow and usePlayer's two predicates.
                active={player.isActive(song.id)}
                /*
                  Pressing the title raises the lyrics preview, as it does in the Library —
                  it used to be a link into the Studio workspace, so tapping a track's name
                  inside a playlist left the playlist. The Studio is the row's menu item
                  below, and the same route it always was.
                */
                onSelect={setLyricsTarget}
                onOpenInStudio={(s) => navigate(paths.song(s.id))}
                /*
                Two gestures on the one control, and which one it is depends on whether
                this row is already the loaded song.

                Not loaded: start the playlist HERE — the whole playlist, from this track
                — which is the note at the top of this file.

                Already loaded: pause or resume it, and touch nothing else. Restarting
                the queue instead would be wrong twice over. It would throw away your
                position in the track, and — because playPlaylist rebuilds the queue from
                the playlist's current order — it would silently discard the queue you
                were actually hearing. Pressing pause is not a request to re-open the
                playlist.
              */
                onPlay={() =>
                  player.isActive(song.id) ? player.toggle(song.id) : playPlaylist(playlist, i)
                }
                /*
                  The same queue, from the same track, plus the floating bar — the row's
                  explicit request for it, since `onPlay` above deliberately does not raise
                  one. `tracks` is this playlist in its current order, which is exactly what
                  playPlaylist builds its queue from, so the bar's Prev/Next walk the play
                  order this screen is showing. There is no separate mini-player queue.
                */
                onPlayInMiniPlayer={() =>
                  player.playQueueInMiniPlayer(
                    tracks.map((t) => t.id),
                    i
                  )
                }
                // Undefined at the ends, which is what renders the button disabled —
                // PlaylistTrackRow keeps it in place rather than removing it.
                onMoveUp={i > 0 ? () => moveSong(playlist, song, -1) : undefined}
                onMoveDown={i < tracks.length - 1 ? () => moveSong(playlist, song, 1) : undefined}
                onRemove={() => removeSong(playlist, song)}
                playlistName={playlist.name}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
